import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import useWithUrlTenant from "src/hooks/useTenant";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";

import { AppCurrentVisits } from "../sections/@dashboard/app";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Iconify from "src/components/iconify/Iconify";
import { get, post } from "src/utils/networkUtils";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AppWidgetSummaryPct from "src/sections/@dashboard/app/AppWidgetSummaryPct";
import { getPositionsForSport } from "src/utils/scoreablesDashboardUtils";

const getChartDataForTier = (scoreablesStats, tier, positions) => {
  const teamCount = scoreablesStats[`${tier}TeamCount`];
  const chartData = [{ label: "Team", value: teamCount }];

  positions.forEach((position) => {
    const positionCount =
      scoreablesStats[`${tier}Pos${position.pos}Count`] || 0;
    chartData.push({ label: position.label, value: positionCount });
  });

  return chartData;
};

export default function ScoreablesDashboardPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [scoreablesStats, setScoreablesStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncingTraining, setSyncingTraining] = useState(false);
  const [syncingMarketplace, setSyncingMarketplace] = useState(false);
  const [fixingCardsTraining, setFixingCardsTraining] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const positions = getPositionsForSport(selectedCompetition?.sport);
  const withTenant = useWithUrlTenant();

  // Use the new hook for tenant competitions
  const { tenantCompetitions, tenantId } = useTenantCompetitions();

  useEffect(() => {
    if (tenantCompetitions.length > 0) {
      setSelectedCompetition(tenantCompetitions[0]);
    }
  }, [tenantCompetitions]);

  // Reset scoreables stats when tenantId changes to avoid stale data
  useEffect(() => {
    setScoreablesStats(null);
  }, [tenantId]);

  useEffect(() => {
    // Check if selectedCompetition exists in current tenantCompetitions
    const isValidCompetition =
      selectedCompetition &&
      tenantCompetitions.some(
        (c) => c.competitionId === selectedCompetition.competitionId
      );

    if (isValidCompetition) {
      setLoading(true);
      get(
        withTenant(`/admin/scoreables/stats?sport=${selectedCompetition.sport}`)
      )
        .then(function (response) {
          setScoreablesStats(response.stats);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching scoreables stats:", error);
          setLoading(false);
        });
    }
  }, [selectedCompetition, withTenant, tenantId, tenantCompetitions]);

  const downloadFptsHandler = (position) => {
    get(
      withTenant(`/admin/download-fpts/${position}`, { responseType: "blob" })
    ).then(function (response) {
      const downloadUrl = window.URL.createObjectURL(new Blob([response]));

      const link = document.createElement("a");

      link.href = downloadUrl;

      link.setAttribute("download", "position-fpts.csv");

      document.body.appendChild(link);

      link.click();

      link.remove();
    });
  };
  const syncTrainingHandler = () => {
    setSyncingTraining(true);
    post(withTenant("/admin/cards/refill-training-cache"))
      .then(function (response) {
        setSyncingTraining(false);
        window.location.reload();
      })
      .catch((error) => {
        console.error("Error syncing training cache:", error);
        setSyncingTraining(false);
      });
  };


  const syncMarketplaceCardsSearch = () => {
    setSyncingMarketplace(true);
    post(withTenant("/admin/marketplace/refill-search"))
      .then(function (response) {
        console.log("Marketplace cards search synced:", response);
        setSyncingMarketplace(false);
        window.location.reload();
      })
      .catch((error) => {
        console.error("Error syncing marketplace cards search:", error);
        setSyncingMarketplace(false);
      });
  };
  const fixCardsTraining = () => {
    setFixingCardsTraining(true);
    post(withTenant("/admin/training/fix-user-training-data"))
      .then(function (response) {
        console.log("Cards training fixed:", response);
        setFixingCardsTraining(false);

        // Show results in alert
        const { fixedCards, totalCardsFixed } = response;
        let message = `Successfully fixed ${totalCardsFixed} cards!\n\n`;
        if (fixedCards && fixedCards.length > 0) {
          message += "Details:\n";
          fixedCards.forEach((card, index) => {
            message += `${index + 1}. Card ${card.cardId} - Position: ${card.position}, Stat: ${card.statType}, Deleted: ${card.deletedCount}/${card.totalTrainings}\n`;
          });
        }
        alert(message);
      })
      .catch((error) => {
        console.error("Error fixing cards training:", error);
        setFixingCardsTraining(false);
        alert("Error fixing cards training: " + (error.message || "Unknown error"));
      });
  };

  return (
    <>
      <Helmet>
        <title> Scoreables </title>
      </Helmet>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 5,
          }}
        >
          <Typography variant="h4">Scoreables</Typography>
          {tenantCompetitions.length > 0 && (
            <FormControl sx={{ minWidth: 320 }} size="small">
              <InputLabel id="competition-select-label">Competition</InputLabel>
              <Select
                labelId="competition-select-label"
                id="competition-select"
                value={selectedCompetition?.competitionId || ""}
                label="Competition"
                onChange={(event) => {
                  const id = Number(event.target.value);
                  setSelectedCompetition(
                    tenantCompetitions.find(
                      (c) => +c.competitionId === id
                    )
                  )
                }
                }
              >
                {tenantCompetitions.map((competition) => (
                  <MenuItem
                    value={competition.competitionId}
                    key={`competition-${competition.competitionId}`}
                  >
                    {competition.competitionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        {tenantCompetitions.length > 0 && (
          <Grid container sx={{ mb: 3 }} spacing={2}>
            {tenantCompetitions.map((competition) => (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${competition.competitionId}-teams`}
                >
                  <Button
                    onClick={() =>
                      navigate(
                        withTenant(
                          `/dashboard/scoreables/competition-scoreables?competition_id=${competition.competitionId}&is_international=false&ratings=false`
                        )
                      )
                    }
                    variant="contained"
                    fullWidth
                    endIcon={
                      <Iconify
                        icon={"eva:person-done-outline"}
                        width={24}
                        height={24}
                      />
                    }
                  >
                    {competition.competitionName} Scoreables (Teams)
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${competition.competitionId}-ratings`}
                >
                  <Button
                    onClick={() =>
                      navigate(
                        withTenant(
                          `/dashboard/scoreables/competition-scoreables?competition_id=${competition.competitionId}&is_international=false&ratings=true`
                        )
                      )
                    }
                    variant="contained"
                    fullWidth
                    endIcon={
                      <Iconify
                        icon={"eva:person-done-outline"}
                        width={24}
                        height={24}
                      />
                    }
                  >
                    {competition.competitionName} Scoreables (Ratings)
                  </Button>
                </Grid>
              </>
            ))}
          </Grid>
        )}

        {scoreablesStats === null || loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center" }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Alert sx={{ marginBottom: 2 }} severity="info">
                  {` Active: ${scoreablesStats.allActiveCount} | Overall: ${scoreablesStats.allCount} `}
                </Alert>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3} md={3}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <AppWidgetSummaryPct
                      title={`GOLD (${scoreablesStats.goldCount})`}
                      total={Math.round(
                        (+scoreablesStats.goldCount /
                          +scoreablesStats.allActiveCount) *
                        100
                      )}
                      icon={"eva:person-done-outline"}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppCurrentVisits
                      title=""
                      chartData={getChartDataForTier(
                        scoreablesStats,
                        "gold",
                        positions
                      )}
                      chartColors={[
                        theme.palette.primary.main,
                        theme.palette.info.main,
                        theme.palette.warning.main,
                        theme.palette.error.main,
                        theme.palette.secondary.main,
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <AppWidgetSummaryPct
                      title={`SILVER (${scoreablesStats.silverCount})`}
                      total={Math.round(
                        (+scoreablesStats.silverCount /
                          +scoreablesStats.allActiveCount) *
                        100
                      )}
                      color="primary"
                      icon={"eva:person-done-outline"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppCurrentVisits
                      title=""
                      chartData={getChartDataForTier(
                        scoreablesStats,
                        "silver",
                        positions
                      )}
                      chartColors={[
                        theme.palette.primary.main,
                        theme.palette.info.main,
                        theme.palette.warning.main,
                        theme.palette.error.main,
                        theme.palette.secondary.main,
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <AppWidgetSummaryPct
                      title={`BASE (${scoreablesStats.baseCount})`}
                      total={Math.round(
                        (+scoreablesStats.baseCount /
                          +scoreablesStats.allActiveCount) *
                        100
                      )}
                      color="error"
                      icon={"eva:person-done-outline"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppCurrentVisits
                      title=""
                      chartData={getChartDataForTier(
                        scoreablesStats,
                        "base",
                        positions
                      )}
                      chartColors={[
                        theme.palette.primary.main,
                        theme.palette.info.main,
                        theme.palette.warning.main,
                        theme.palette.error.main,
                        theme.palette.secondary.main,
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <AppWidgetSummaryPct
                      title={`ALL STAR (${scoreablesStats.allStarCount})`}
                      total={Math.round(
                        (+scoreablesStats.allStarCount /
                          +scoreablesStats.allActiveCount) *
                        100
                      )}
                      color="error"
                      icon={"eva:person-done-outline"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppCurrentVisits
                      title=""
                      chartData={getChartDataForTier(
                        scoreablesStats,
                        "allStar",
                        positions
                      )}
                      chartColors={[
                        theme.palette.primary.main,
                        theme.palette.info.main,
                        theme.palette.warning.main,
                        theme.palette.error.main,
                        theme.palette.secondary.main,
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        <Grid container sx={{ mt: 3 }} gap={3}>
          <Grid item>
            <Button
              onClick={() => downloadFptsHandler(0)}
              variant="contained"
              endIcon={
                <Iconify
                  icon={"eva:arrow-circle-down-outline"}
                  width={20}
                  height={20}
                />
              }
            >
              Teams FPTS
            </Button>
          </Grid>
          {positions.map((position, index) => (
            <Grid item key={position.pos}>
              <Button
                onClick={() => downloadFptsHandler(position.value)}
                variant="contained"
                endIcon={
                  <Iconify
                    icon={"eva:arrow-circle-down-outline"}
                    width={20}
                    height={20}
                  />
                }
              >
                {position.label} FPTS
              </Button>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ height: 20, m: 2 }} >
          <Button
            onClick={() => syncTrainingHandler()}
            variant="contained"
            disabled={syncingTraining}
            startIcon={
              syncingTraining ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify
                  icon={"eva:refresh-outline"}
                  width={20}
                  height={20}
                />
              )
            }
          >
            {syncingTraining ? "Syncing..." : "Sync Training Cache"}
          </Button>
          <Button
            sx={{ ml: 2 }}
            onClick={() => syncMarketplaceCardsSearch()}
            variant="contained"
            disabled={syncingMarketplace}
            startIcon={
              syncingMarketplace ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify
                  icon={"eva:search-outline"}
                  width={20}
                  height={20}
                />
              )
            }
          >
            {syncingMarketplace ? "Syncing..." : "Sync Marketplace Cards Search"}
          </Button>

          <Button
            sx={{ ml: 2 }}
            onClick={() => fixCardsTraining()}
            variant="contained"
            disabled={fixingCardsTraining}
            startIcon={
              fixingCardsTraining ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify
                  icon={"eva:settings-outline"}
                  width={20}
                  height={20}
                />
              )
            }
          >
            {fixingCardsTraining ? "Fixing..." : "Fix Cards Training"}
          </Button>
        </Box>

      </Container >
    </>
  );
}
